import {ReactNode, useEffect, useState} from 'react';

import {isNotNil} from 'ramda';

import {useComponentsTheme} from '../../../hooks/useComponentsTheme';
import {Portal} from '../../Portal/components/Portal';
import {PortalManager} from '../../Portal/components/PortalManager';
import {Dropdown} from '../Dropdown';
import {OpenDropdownMenuProps} from '../types/openDropdownMenuProps';
import {CLOSE_DROPDOWN_EVENT} from '../utils/closeDropdownEvent';
import {OPEN_DROPDOWN_EVENT} from '../utils/openDropdownEvent';

export interface DropdownProviderProps {
  children: ReactNode;
}

type OpendDropdownData = {
  clientX: number;
  clientY: number;
  content: ReactNode;
  menuProps?: OpenDropdownMenuProps;
};

type OpenDropdownEventDetail = {
  position: Pick<OpendDropdownData, 'clientX' | 'clientY'>;
  content: ReactNode;
  options?: OpenDropdownMenuProps;
};

type CloseDropdownEventDetail = {
  id: string;
};

export function DropdownProvider(props: DropdownProviderProps) {
  const theme = useComponentsTheme();
  const [opendDropdownData, setOpendDropdownData] = useState<OpendDropdownData | undefined>();

  useEffect(
    function subscribeToOpenDropdown() {
      const listener = (event: Event) => {
        if (isCustomEvent<OpenDropdownEventDetail>(event)) {
          setOpendDropdownData({
            ...event.detail.position,
            content: event.detail.content,
            menuProps: event.detail.options,
          });
        }
      };

      window.addEventListener(OPEN_DROPDOWN_EVENT, listener);

      return () => {
        window.removeEventListener(OPEN_DROPDOWN_EVENT, listener);
      };
    },
    [setOpendDropdownData]
  );

  useEffect(
    function subscribeToCloseDropdown() {
      const handleDropdownClose = (event: Event) => {
        // sanctioned deviation: options-less dropdown close is a no-op instead of the legacy crash
        if (
          isCustomEvent<CloseDropdownEventDetail>(event) &&
          event.detail.id === opendDropdownData?.menuProps?.id
        ) {
          setOpendDropdownData(undefined);
        }
      };
      window.addEventListener(CLOSE_DROPDOWN_EVENT, handleDropdownClose);
      return () => {
        window.removeEventListener(CLOSE_DROPDOWN_EVENT, handleDropdownClose);
      };
    },
    [opendDropdownData]
  );

  return (
    <>
      <PortalManager zIndex={theme.zIndices.DROPDOWN_COMPONENT}>
        <Portal>
          <div
            style={{
              top: opendDropdownData?.clientY,
              left: opendDropdownData?.clientX,
              position: 'absolute',
            }}
          >
            <Dropdown
              isOpen={isNotNil(opendDropdownData)}
              onClose={() => setOpendDropdownData(undefined)}
              {...opendDropdownData?.menuProps}
            >
              {opendDropdownData?.content}
            </Dropdown>
          </div>
        </Portal>
      </PortalManager>
      {props.children}
    </>
  );
}

function isCustomEvent<Detail>(event: Event): event is CustomEvent<Detail> {
  return 'detail' in event;
}
